/** CSS contract: css-library/components/02-containers/filter-menu.css. */
import type { Ref } from 'react';
import { usePopoverEntered } from '../lib/usePopoverEntered';
import { useHoverDisclosure } from '../lib/useHoverDisclosure';
import { ButtonControl } from './ButtonControl';
import { useModalPopover, usePopoverContainer } from '../lib/modalContext';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  RefObject,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import { cx } from '../lib/cx';
import { mergeRefs } from '../lib/merge-refs';
import { centeredLeft, placePopover, POPOVER_EDGE, POPOVER_MIN_HEIGHT, POPOVER_MAX_HEIGHT } from '../lib/popoverPlace';
import { Button } from './Button';
import type { ButtonProps } from './Button';
import { Icon } from './Icon';
import { ScrollBar } from './ScrollBar';

export type DismissIgnore = readonly (string | RefObject<Element | null>)[];

function matchesDismissIgnore(target: Node, ignore: DismissIgnore | undefined): boolean {
  return ignore?.some((item) => typeof item === 'string'
    ? target instanceof Element && Boolean(target.closest(item))
    : item.current?.contains(target)) ?? false;
}

const MenuContext = createContext<{ close: () => void; presentation: 'popover' | 'pill' } | null>(null);

function MenuContent({ children, headerHeight = 0 }: { children: ReactNode; headerHeight?: number }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  return <>
    <div className="menu-scroll" data-scroll-host ref={scrollerRef}>{children}</div>
    <ScrollBar scrollerRef={scrollerRef} style={{ top: headerHeight + 6 }} />
  </>;
}

export interface MenuTriggerRenderProps {
  open: boolean;
  triggerProps: {
    ref: (node: HTMLButtonElement | null) => void;
    type: 'button';
    'aria-haspopup'?: 'menu' | 'listbox';
    'data-hover-disclosure': true;
    'aria-expanded': boolean;
    'aria-controls': string;
    'aria-hidden'?: boolean;
    tabIndex?: number;
    onClick: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  };
}

export interface MenuProps extends HTMLAttributes<HTMLDivElement> {
  presentation?: 'popover' | 'pill';
  retainTrigger?: boolean;
  panelRole?: 'menu' | 'listbox' | 'search';
  open?: boolean;

  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode | ((props: MenuTriggerRenderProps) => ReactNode);
  triggerButtonProps?: Omit<ButtonProps, 'onClick' | 'children'>;
  isList?: boolean;
  children?: ReactNode;
  dismissIgnore?: DismissIgnore;
  /** `end` puts the popover under the trigger's end instead of its centre. */
  align?: 'center' | 'end';
  panelClassName?: string;
}

export function Menu(
  { ref, open: openProp,
    presentation = 'popover',
    retainTrigger = false,
    panelRole = 'menu',
    onOpenChange,
    trigger,
    triggerButtonProps,
    isList = false,
    dismissIgnore,
    align = 'center',
    panelClassName,
    className,
    children,
    ...rest
  }: MenuProps & { ref?: Ref<HTMLButtonElement> },
) {
  const popoverContainer = usePopoverContainer();
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? openProp : internalOpen;
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const anchorRect = useRef<DOMRect | null>(null);
  const restoreTriggerFocus = useRef(false);
  const menuId = useId();
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight?: number; minWidth?: number; ready?: boolean; headerHeight?: number }>({ top: 0, left: 0 });
  const entered = usePopoverEntered(open);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  const hover = useHoverDisclosure(open, setOpen);
  const close = hover.dismiss;
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const pop = panelRef.current;
    if (!trigger || !pop) return;
    const rect = (presentation === 'pill' ? trigger.closest('.pill') ?? trigger : trigger).getBoundingClientRect();
    anchorRect.current = rect;
    const width = Math.max(pop.getBoundingClientRect().width, presentation === 'pill' ? rect.width : 0);
    const cap = isList ? Number.POSITIVE_INFINITY : POPOVER_MAX_HEIGHT;
    const scroller = pop.querySelector<HTMLElement>('.menu-scroll')!;
    const height = Math.min(scroller.scrollHeight + pop.clientHeight - scroller.clientHeight + 2, cap);
    if (presentation === 'pill') {
      const vh = document.documentElement.clientHeight;
      const below = vh - POPOVER_EDGE - rect.top;
      const above = rect.bottom - POPOVER_EDGE;
      const upwards = below < POPOVER_MIN_HEIGHT && above > below;
      const maxHeight = Math.max(rect.height, upwards ? above : below);
      setPos({ top: upwards ? Math.max(POPOVER_EDGE, rect.bottom - Math.min(height, maxHeight)) : rect.top,
        left: retainTrigger ? Math.max(POPOVER_EDGE, Math.min(rect.left, document.documentElement.clientWidth - width - POPOVER_EDGE)) : centeredLeft(rect, width), maxHeight, minWidth: rect.width, ready: true, headerHeight: retainTrigger ? rect.height : undefined });
      return;
    }
    const placed = placePopover(rect, width, height);
    setPos({
      top: placed.top,
      left: align === 'end' ? Math.max(POPOVER_EDGE, rect.right - POPOVER_EDGE - width) : placed.left,
      maxHeight: Math.min(placed.maxHeight, cap),
    });
  }, [align, isList, presentation, retainTrigger]);
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition, children]);
  useLayoutEffect(() => {
    if (!open && presentation === 'pill') setPos((previous) => previous.ready ? { ...previous, ready: false } : previous);
    if (!open && restoreTriggerFocus.current) {
      const frame = requestAnimationFrame(() => {
        restoreTriggerFocus.current = false;
        triggerRef.current?.focus({ preventScroll: true });
      });
      return () => cancelAnimationFrame(frame);
    }
    if (open && !hover.preview && presentation === 'pill' && pos.ready) {
      const frame = requestAnimationFrame(() => {
        const panel = panelRef.current;
        (panel?.querySelector<HTMLButtonElement>('[aria-checked="true"]:not(:disabled), [aria-selected="true"]:not(:disabled)') ?? panel?.querySelector<HTMLButtonElement>('button:not(:disabled)'))?.focus();
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [open, presentation, retainTrigger, pos.ready, hover.preview]);
  const closeAndFocus = useCallback(() => {
    restoreTriggerFocus.current = true;
    close();
  }, [close]);
  useModalPopover(open, presentation === 'pill' ? closeAndFocus : close);
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: globalThis.MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      if (matchesDismissIgnore(target, dismissIgnore)) return;
      close();
    }
    function onScroll(e: Event) {
      if (panelRef.current?.contains(e.target as Node)) return;
      if (matchesDismissIgnore(e.target as Node, dismissIgnore)) return;
      if (presentation === 'pill') {
        const trigger = triggerRef.current;
        const current = (trigger?.closest('.pill') ?? trigger)?.getBoundingClientRect();
        const previous = anchorRect.current;
        if (current && previous && current.top === previous.top && current.left === previous.left
          && current.width === previous.width && current.height === previous.height) return;
      }
      close();
    }
    function onResize() {
      flushSync(() => reposition());
    }
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close, reposition, dismissIgnore, presentation]);
  const { preview, keep, leave, pin } = hover;
  useEffect(() => {
    if (!open || !preview || !dismissIgnore) return;
    const track = (event: globalThis.PointerEvent) => {
      if (event.target instanceof Node && matchesDismissIgnore(event.target, dismissIgnore)) keep();
      else if (!wrapRef.current?.contains(event.target as Node) && !panelRef.current?.contains(event.target as Node)) leave();
    };
    const pinCompanion = (event: globalThis.PointerEvent) => {
      if (event.target instanceof Node && matchesDismissIgnore(event.target, dismissIgnore)) pin();
    };
    document.addEventListener('pointerover', track, true);
    document.addEventListener('pointerdown', pinCompanion, true);
    return () => {
      document.removeEventListener('pointerover', track, true);
      document.removeEventListener('pointerdown', pinCompanion, true);
    };
  }, [open, preview, keep, leave, pin, dismissIgnore]);
  function handleTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      hover.pin();
    } else if (open && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (presentation === 'pill') closeAndFocus();
      else { close(); triggerRef.current?.focus(); }
    }
  }

  function handlePanelKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.defaultPrevented) return;
    if (e.target instanceof HTMLInputElement && (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete')) hover.pin();
    if (presentation === 'pill' && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
      const choices = [...(panelRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitemradio"]:not(:disabled), button[role="option"]:not(:disabled)') ?? [])];
      if (!choices.length) return;
      const current = choices.indexOf(document.activeElement as HTMLButtonElement);
      const next = e.key === 'Home' ? 0 : e.key === 'End' ? choices.length - 1
        : (current + (e.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length;
      e.preventDefault();
      e.stopPropagation();
      choices[next].focus();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (presentation === 'pill') closeAndFocus();
      else { close(); triggerRef.current?.focus(); }
    }
  }

  const mergedRef = mergeRefs(triggerRef, ref);
  const { isActive: triggerIsActive, onKeyDown: triggerOnKeyDown, ...restTriggerButtonProps } = triggerButtonProps ?? {};
  const triggerNode =
    typeof trigger === 'function' ? (
      trigger({
        open,
        triggerProps: {
          ref: mergedRef,
          type: 'button',
          'aria-haspopup': panelRole === 'search' ? undefined : panelRole,
          'data-hover-disclosure': true,
          'aria-expanded': open,
          'aria-controls': menuId,
          ...(presentation === 'pill' && !retainTrigger && open && pos.ready ? { 'aria-hidden': true, tabIndex: -1 } : {}),
          onClick: hover.toggle,
          onKeyDown: handleTriggerKeyDown,
        },
      })
    ) : (
      <Button
        ref={mergedRef}
        {...restTriggerButtonProps}
        isActive={open || triggerIsActive}
        data-hover-disclosure
        aria-haspopup={panelRole === 'search' ? undefined : panelRole}
        aria-expanded={open}
        aria-controls={menuId}
        aria-hidden={presentation === 'pill' && !retainTrigger && open && pos.ready ? true : undefined}
        tabIndex={presentation === 'pill' && !retainTrigger && open && pos.ready ? -1 : restTriggerButtonProps.tabIndex}
        onClick={hover.toggle}
        onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
          triggerOnKeyDown?.(e);
          if (e.defaultPrevented) return;
          handleTriggerKeyDown(e);
        }}
      >
        {trigger}
      </Button>
    );
  return (
    <div ref={wrapRef} className={cx(className, presentation === 'pill' && 'pill-menu-anchor', retainTrigger && 'pill-menu-anchor--retained', presentation === 'pill' && open && pos.ready && 'is-open')} {...rest}
      onPointerEnter={(event) => {
        if (event.currentTarget.contains(event.target as Node) && !triggerRef.current?.disabled) hover.onPointerEnter(event);
      }}
      onPointerLeave={(event) => { if (event.currentTarget.contains(event.target as Node)) hover.onPointerLeave(); }}
      onPointerMove={(event) => { if (event.currentTarget.contains(event.target as Node)) hover.onPointerMove(event); }}>
      {triggerNode}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={menuId}
            role={panelRole}
            aria-hidden={presentation === 'pill' && !pos.ready ? true : undefined}
            className={cx('menu-panel', presentation === 'pill' ? 'pill pill-menu' : 'select-popover', isList && 'filter-menu-list', retainTrigger && 'pill-menu--retained', entered && 'is-open', panelClassName)}
            style={{
              position: retainTrigger ? 'absolute' : 'fixed',
              opacity: presentation === 'pill' && !pos.ready ? 0 : undefined,
              pointerEvents: presentation === 'pill' && !pos.ready ? 'none' : undefined,
              top: retainTrigger ? -1 : pos.top,
              left: retainTrigger ? -1 : pos.left,
              maxHeight: pos.maxHeight,
              width: retainTrigger && triggerRef.current?.closest('.pill--tools') ? pos.minWidth : undefined,
              paddingTop: pos.headerHeight,
              minWidth: presentation === 'pill' ? pos.minWidth : undefined,
            }}
            onKeyDown={handlePanelKeyDown}
            onPointerEnter={hover.onPointerEnter} onPointerLeave={hover.onPointerLeave}
            onPointerDown={() => { if (presentation !== 'pill' || retainTrigger) hover.pin(); }}
          >
            <MenuContext.Provider value={{ close: presentation === 'pill' ? closeAndFocus : close, presentation }}><MenuContent headerHeight={pos.headerHeight}>{children}</MenuContent></MenuContext.Provider>
          </div>,
          (retainTrigger ? triggerRef.current?.closest('.pill') ?? wrapRef.current : null) ?? popoverContainer ?? document.body,
        )}
    </div>
  );
}

export interface MenuRowProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> {
  isSelected?: boolean;
  isCompleted?: boolean;
  label: ReactNode;
  count?: number;
  isOpen?: boolean;
  icon?: string;
  onSelect?: () => void;
}

export function MenuRow(
  { ref, label, count, isOpen, icon, isSelected, isCompleted, onSelect, className, onClick, type = 'button', ...rest }: MenuRowProps & { ref?: Ref<HTMLButtonElement> },
) {
  const expandable = isOpen !== undefined;
  const menuCtx = useContext(MenuContext);
  const pill = menuCtx?.presentation === 'pill';
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onClick?.(event);
    if (event.defaultPrevented) return;
    onSelect?.();
    if (!expandable) menuCtx?.close();
  }

  return (
    <ButtonControl
      ref={ref}
      type={type}
      role="menuitem"
      aria-description={isCompleted ? "Abgeschlossen" : undefined}
      aria-haspopup={expandable ? 'menu' : undefined}
      aria-expanded={expandable ? isOpen : undefined}
      className={cx(pill ? 'btn pill-menu-option' : 'filter-menu-row', isOpen && 'is-open', isSelected && 'is-selected', pill && isSelected && 'is-active', isCompleted && 'is-completed', className)}
      onClick={handleClick}
      {...rest}
    >
      {icon && <Icon name={icon} style={pill ? undefined : { width: 16, height: 16, flexShrink: 0, color: 'var(--color-muted-foreground)' }} />}
      <span className="filter-menu-row-label">{label}</span>
      {isCompleted && <Icon name="i-check" className="menu-completion" aria-label="Abgeschlossen" />}
      {count != null && count > 0 && <span className="filter-menu-row-count">{count}</span>}
      {expandable && <Icon name="i-chevron-right" className="filter-menu-row-chevron" />}
    </ButtonControl>
  );
}

export interface MenuFlyoutPlacement {
  top: number;
  left: number;
  maxHeight?: number;
}

export interface MenuFlyoutProps extends HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean;
  placement?: MenuFlyoutPlacement;
}

export function MenuFlyout(
  { ref, isOpen = false, placement, className, children, ...rest }: MenuFlyoutProps & { ref?: Ref<HTMLDivElement> },
) {
  return (
    <div
      ref={ref}
      role="menu"
      className={cx('menu-panel', 'pill pill-menu', 'filter-menu-flyout', isOpen && 'is-open', className)}
      style={placement && { position: 'fixed', top: placement.top, left: placement.left, maxHeight: placement.maxHeight }}
      {...rest}
    >
      <MenuContent>{children}</MenuContent>
    </div>
  );
}
